import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuthStore } from '../../stores/auth-store';
import { environment } from '../../config/environment';

/**
 * Simple login page for testing SuperAdmin access
 * This is a placeholder - in production this would integrate with actual auth
 */
export function Login() {
  const [email, setEmail] = useState('admin@cueflow.cc');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { setUser } = useAuthStore();
  const navigate = useNavigate();

  const handleSuperAdminLogin = async () => {
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Use the proper superadmin login endpoint
      const apiUrl = `${environment.api.baseUrl}/auth/superadmin/login`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed');
      }

      const loginData = await response.json();

      // Validate response structure
      if (!loginData.token || !loginData.user) {
        throw new Error('Invalid login response structure');
      }

      // Store the JWT token in localStorage for API calls
      localStorage.setItem('authToken', loginData.token);

      // Set user in auth store
      setUser({
        id: loginData.user.id || 'unknown',
        email: loginData.user.email || email,
        name: 'SuperAdmin User',
        role: 'SuperAdmin',
        studioIds: [],
      });

      // Redirect to SuperAdmin dashboard
      navigate('/superadmin');
    } catch (err: any) {
      // If SuperAdmin login fails, provide more context
      if (err.message === 'Invalid SuperAdmin credentials') {
        setError('Invalid credentials. Please check your email and password.');
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = (demoRole: 'platform_admin' | 'studio_owner') => {
    // Demo login for testing - this doesn't use real authentication
    // Map demo roles to actual backend roles
    const role = demoRole === 'platform_admin' ? 'SuperAdmin' : 'StudioOwner';
    
    // For SuperAdmin demo, generate a mock JWT token
    if (demoRole === 'platform_admin') {
      // Create a mock token for development
      const mockToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiJzdXBlcmFkbWluIiwiZW1haWwiOiJhZG1pbkBjdWVmbG93LmNjIiwicm9sZSI6IlN1cGVyQWRtaW4iLCJpYXQiOjE2OTk5OTk5OTl9.mock-superadmin-token';
      localStorage.setItem('authToken', mockToken);
    }
    
    setUser({
      id: demoRole === 'platform_admin' ? 'superadmin' : '1',
      email: demoRole === 'platform_admin' ? 'admin@cueflow.cc' : 'demo@example.com',
      name: demoRole === 'platform_admin' ? 'SuperAdmin User' : 'Studio Owner',
      role: role,
      studioIds: demoRole === 'platform_admin' ? [] : ['studio-1'],
    });

    // Navigate to appropriate dashboard
    navigate(demoRole === 'platform_admin' ? '/superadmin' : '/studio');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>
            Demo login for testing SuperAdmin access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              SuperAdmin Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter SuperAdmin email"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={isLoading}
              onKeyPress={(e) => e.key === 'Enter' && handleSuperAdminLogin()}
            />
          </div>
          
          <div className="space-y-2">
            <Button 
              onClick={handleSuperAdminLogin}
              className="w-full"
              variant="destructive"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login as SuperAdmin'}
            </Button>
            {environment.development && (
              <Button 
                onClick={() => handleDemoLogin('platform_admin')}
                className="w-full"
                variant="default"
                disabled={isLoading}
              >
                Demo Login as SuperAdmin (Dev Only)
              </Button>
            )}
            <Button 
              onClick={() => handleDemoLogin('studio_owner')}
              className="w-full"
              variant="outline"
              disabled={isLoading}
            >
              Demo Login as Studio Owner
            </Button>
          </div>

          <div className="text-center text-xs text-gray-500">
            <p>Use SuperAdmin credentials to access platform management.</p>
            <p>Studio Owner demo login doesn't require authentication.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}